import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  CustomIdInputTypesIdLength,
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
  IIrembogovBasicLabelKeyPair,
  LOCATION_WIDGET_LEVELS,
  alphaNanoIdUtil,
} from '@irembo-andela/irembogov3-common';
import { ValidationTypeDefaultMessage } from '../../../core/utils/form-fields-formly-configurations/form-builder-validation-type-message.utils';
import * as LocationWidgetUtils from '../../../core/utils/form-fields-formly-configurations/location-widget-components-generator.util';
import { buildSlotWidgetField } from '../../../core/utils/form-fields-formly-configurations/slot-booking-component-generator.utils';

/**
 * Palette catalog for the V2 form builder.
 *
 * The field/widget definitions below are COPIED VERBATIM from V1's
 * form-field-types-list and form-field-widgets-list components so the V2 palette
 * shows the exact same items with the exact same default configs. V1 is left
 * untouched on purpose; this is a deliberate (temporary) duplicate until a
 * shared registry is reintroduced. If V1's lists change, mirror them here.
 *
 * `IFbPaletteItem` is the V2-facing shape; `toFbPaletteItems` converts V1's
 * `{ label, key }` pairs into it.
 */

export interface IFbPaletteItem {
  displayName: string;
  type: string;
  category: 'field' | 'widget';
  icon: string;
  // The full V1 FormlyFieldConfig template used as the default when the item is
  // added to the canvas (clone before mutating).
  config: FormlyFieldConfig;
}

// Single muted icon for all items, matching V1's list styling.
const FB_PALETTE_ITEM_ICON = 'fa-solid fa-mattress-pillow';

function buildFieldPaletteSource(): IIrembogovBasicLabelKeyPair<FormlyFieldConfig>[] {
  const randomKey = alphaNanoIdUtil(5);
  return [
    {
      label: 'Attachment',
      key: {
        key: 'Attachment',
        type: EIremboFormlyFieldTypes.customfileupload,
        props: {
          required: false,
          label: 'File Attachment',
          minimumUploadSize: 100,
          maximumUploadSize: 400,
          placeholder: 'Select file to upload',
          allowedFormats: [],
        },
        validation: {
          messages: {
            minimumUploadSize: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.minimumUploadSize
            ),
            maximumUploadSize: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.maximumUploadSize
            ),
          },
        },
      },
    },
    {
      label: 'Checkbox',
      key: {
        key: 'Checkbox',
        type: EIremboFormlyFieldTypes.checkbox,
        props: {
          label: 'Checkbox',
          default: false,
          pattern: 'true',
          disabled: false,
          required: false,
          placeholder: 'Checkbox',
          hideLabel: true,
          description: '',
          indeterminate: false,
        },
      },
    },
    {
      label: 'Date Picker',
      key: {
        key: 'datepicker',
        type: EIremboFormlyFieldTypes.customdatepicker,
        props: {
          label: 'Date Picker',
          disabled: false,
          required: false,
          placeholder: 'Date Picker',
        },
      },
    },
    {
      label: 'Dropdown Selector',
      key: {
        key: 'DropdownSelector',
        type: EIremboFormlyFieldTypes.customdropdown,
        props: {
          label: 'Dropdown Selector',
          disabled: false,
          required: false,
          placeholder: 'Dropdown Selector',
          options: [
            { value: 'option1Key', label: 'Option 1' },
            { value: 'option2Key', label: 'Option 2' },
            { value: 'option3Key', label: 'Option 3' },
            { value: 'option4Key', label: 'Option 4' },
          ],
          multiple: false,
          bindValue: 'value',
          bindLabel: 'label',
        },
      },
    },
    {
      label: 'Dropdown Selector With Data Fetch',
      key: {
        key: 'DropdownSelectorWithDataFetch',
        type: EIremboFormlyFieldTypes.customdropdown,
        name: 'DropdownSelectorWithDataFetch',
        props: {
          label: 'Dropdown Selector With Data Fetch',
          disabled: false,
          required: false,
          placeholder: 'Dropdown Selector With Data Fetch',
          options: [],
          multiple: false,
          virtualScroll: true,
          useBaseUrl: true,
          dataset: {
            url: '/integration/v1/fetch/sync',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
        },
      },
    },
    {
      label: 'Dropdown Selector With Dataset Non Paginated',
      key: {
        key: 'DropdownSelectorWithDatasetNonPaginated',
        type: EIremboFormlyFieldTypes.customdropdown,
        name: 'DropdownSelectorWithDatasetNonPaginated',
        props: {
          label: 'Dataset Non Paginated',
          disabled: false,
          required: true,
          placeholder: 'Select dataset',
          options: [],
          multiple: false,
          useBaseUrl: true,
          dataset: {
            url: '/admin/v1/datasets/all',
            dataField: 'data',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        },
      },
    },
    {
      label: 'Dropdown Selector With Dataset Paginated',
      key: {
        key: 'DropdownSelectorWithDataset',
        type: EIremboFormlyFieldTypes.customdropdownpaginated,
        name: 'DropdownSelectorWithDataset',
        props: {
          label: 'Dataset',
          disabled: false,
          required: true,
          placeholder: 'Select dataset',
          options: [],
          multiple: false,
          useBaseUrl: true,
          dataset: {
            url: '/admin/v1/dataset-items',
            dataField: 'data.content',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
        },
      },
    },
    {
      label: 'Email Address',
      key: {
        key: 'EmailAddress',
        type: 'input',
        props: {
          label: 'Email address',
          disabled: false,
          required: false,
          placeholder: 'Provide email address',
          hint: '',
          tooltip: '',
          internalDescription: '',
          readonly: false,
          type: 'customemail',
        },
        validation: {
          messages: {
            email: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.email
            ),
          },
        },
        validators: {
          validation: ['email'],
        },
      },
    },
    {
      label: 'Error Component',
      key: {
        key: 'ErrorComponent',
        type: 'customerrorcomponent',
        props: {
          layout: 'inline',
          errorKey: 'genericDataFetch',
          internalDescription: 'Internal Description: Error Component',
        },
      },
    },
    {
      label: 'International Phone No.',
      key: {
        key: 'InternationalPhoneNumber',
        type: EIremboFormlyFieldTypes.customphonenumber,
        props: {
          label: 'Phone Number International',
          required: true,
          id: 'telephone',
          countryNoValidation: true,
          preferredCountries: [],
        },
        validation: {
          messages: {
            required: 'Field is required',
            invalidNumber: 'Provided number is invalid',
            invalidNumberFormat: 'Number format is invalid',
          },
        },
        validators: {
          validation: ['invalidNumberFormat'],
        },
      },
    },

    {
      label: 'Multi-checkbox',
      key: {
        key: 'MultiCheckbox',
        type: EIremboFormlyFieldTypes.multicheckbox,
        props: {
          type: 'array',
          label: 'Multi-Checkbox',
          disabled: false,
          required: false,
          placeholder: 'Multi-Checkbox',
          options: [
            { value: 'CheckBox1Key', label: 'CheckBox 1' },
            { value: 'CheckBox2Key', label: 'CheckBox 2' },
            { value: 'CheckBox3Key', label: 'CheckBox 3' },
            { value: 'CheckBox4Key', label: 'CheckBox 4' },
          ],
        },
      },
    },

    {
      label: 'Number Input',
      key: {
        key: 'NumberInput',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Number Input',
          disabled: false,
          required: false,
          placeholder: 'Number Input',
        },
      },
    },
    {
      label: 'Radio Input',
      key: {
        key: 'TextInput',
        type: EIremboFormlyFieldTypes.radio,
        props: {
          label: 'Radio Input',
          disabled: false,
          required: false,
          placeholder: 'Radio Input',
          options: [
            { value: 'Radio1Key', label: 'Radio 1' },
            { value: 'Radio2Key', label: 'Radio 2' },
            { value: 'Radio3Key', label: 'Radio 3' },
            { value: 'Radio4Key', label: 'Radio 4' },
          ],
        },
      },
    },
    {
      label: 'Section Message With Link',
      key: {
        key: 'SectionMessage',
        type: EIremboFormlyFieldTypes.customsectionmessagewithlink,
        props: {
          links: [
            {
              url: 'http://google.com',
              name: 'Google Link',
              selfTarget: true,
            },
            {
              url: 'http://facebook.com',
              name: 'Facebook',
            },
          ],
          title: 'GuidelInes',
          label: `new_section_${randomKey}`,
          hideLabel: true,
          message: 'This is a message about this section',
          messageType: 'warning',
          extraMessage:
            '<ol><li>Download the form here</li><li>Download the form here</li></ol>',
          extraMessageAsHtml: true,
        },
      },
    },
    {
      label: 'Text Area',
      key: {
        key: 'TextArea',
        type: EIremboFormlyFieldTypes.textarea,
        props: {
          label: 'Text Area',
          disabled: false,
          required: false,
          placeholder: 'Text Area',
          rows: 3,
        },
      },
    },
    {
      label: 'Text Input',
      key: {
        key: 'TextInput',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Text Input',
          disabled: false,
          required: false,
          placeholder: 'Text Input',
        },
      },
    },
    {
      label: 'Repeatable Input',
      key: {
        key: 'subjects',
        type: EIremboFormlyFieldTypes.customrepeater,
        props: {
          addText: 'Add subject',
          uniqueRepeaterValues: ['subject_name'],
        },
        fieldArray: {
          fieldGroup: [
            {
              key: 'subject_name',
              type: 'input',
              props: {
                label: 'Subject',
                required: true,
                placeholder: 'Enter subject',
              },
            },
            {
              key: 'grade',
              type: 'input',
              props: {
                label: 'Grade',
                required: true,
                placeholder: 'Enter grade',
              },
            },
          ],
        },
      },
    },
    {
      label: 'Time Picker',
      key: {
        key: 'Timepicker',
        type: EIremboFormlyFieldTypes.customtimepicker,
        props: {
          label: 'Time Picker',
          disabled: false,
          required: false,
          placeholder: 'Time Picker',
          startHour: 8,
          endHour: 16,
          interval: 30,
        },
      },
    },
    {
      label: 'Table',
      key: {
        key: 'Table',
        type: EIremboFormlyFieldTypes.customdatasettable,
        props: {
          hint: '',
          label: 'Dataset table from details',
          columns: [
            {
              key: 'name',
              label: 'Type',
              sortable: true,
            },
            {
              key: 'value',
              label: 'Type',
              sortable: true,
            },
            {
              key: 'details.parent',
              label: 'Description',
              sortable: true,
            },
          ],
          dataset: {
            url: '/admin/v1/dataset-items',
            params: {
              page: 0,
              size: 10,
            },
            dataField: 'data.content',
            searching: false,
          },
          tooltip: '',
          disabled: false,
          internal: true,
          pageSizes: [10, 50, 100],
          tableClass: 'table',
          useBaseUrl: true,
          placeholder: '',
          isSelectable: true,
          roundedTable: true,
          showDropdown: false,
          isSortVisible: false,
          maxSelectable: 1,
          isSearchVisible: false,
          paginationVisible: false,
          searchPlaceholder: 'Search datasets',
          internalDescription: '',
          selectionOutputPath: '',
          paginationInsideTable: false,
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {},
        },
      },
    },
    {
      label: 'Dynamic Repeatable Input',
      key: {
        key: 'dynamicItems',
        type: EIremboFormlyFieldTypes.customdynamicrepeater,
        props: {
          label: 'Dynamic Items',
          dynamicRepeaterConfig: {
            itemIdField: 'id',
            itemLabelField: 'label',
            emptyStateMessage:
              'No items available. Items will be populated automatically from the data source.',
            enableDebugLogging: false,
          },
        },
        fieldArray: {
          fieldGroup: [
            {
              key: 'fileUpload',
              type: EIremboFormlyFieldTypes.customfileupload,
              props: {
                required: true,
                label: '{{itemLabel}}',
                minimumUploadSize: 100,
                maximumUploadSize: 400,
                placeholder: 'Select file to upload',
                allowedFormats: [],
                disabled: false,
                hint: 'Upload: {{label}}',
              },
              validation: {
                messages: {
                  minimumUploadSize: 'Minimum upload size not met',
                  maximumUploadSize: 'Maximum upload size exceeded',
                },
              },
            },
          ],
        },
      },
    },
    {
      label: 'Currency Format Input',
      key: {
        key: 'CurrencyFormatInput',
        type: EIremboFormlyFieldTypes.customcurrencyformatinput,
        props: {
          label: 'Currency Format Input',
          disabled: false,
          readonly: false,
          required: false,
          placeholder: 'Currency Format Input',
        },
      },
    },
  ];
}

function buildWidgetPaletteSource(): IIrembogovBasicLabelKeyPair<FormlyFieldConfig>[] {
  const randomKey = alphaNanoIdUtil(5);
  return [
    {
      label: 'Application number',
      key: {
        key: 'ApplicationNumber',
        type: EIremboFormlyFieldTypes.customapplicationnumberinput,
        props: {
          label: 'Application number',
          required: true,
          url: '/application/v1/application/by-application-number/details',
          useBaseUrl: true,
          placeholder: 'Application number',
          appNoLength: 19,
          compareKey: 'serviceId',
          compareOperator: '===',
          compareValue: '',
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidAppNo: 'Error message for invalid ID',
            compareStatus: 'Error message for invalid comparison',
          },
        },
      },
    },
    {
      label: 'Cascading Dropdown',
      key: {
        key: 'CascadingDropDown',
        type: EIremboFormlyFieldTypes.customcascadingdropdowns,
        props: {
          required: true,
          label: 'CascadingDropDown',
          configs: [
            {
              key: 'province',
              label: 'Province',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select provinces',
              parent: null,
              required: true,
              dataset: {
                url: 'http://localhost:8000/location/api/v1/provinces',
                dataField: 'data.content',
              },
            },
            {
              key: 'district',
              label: 'District',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select district',
              parent: 'province',
              required: true,
              parentBindKey: 'id',
              dataset: {
                url: 'http://localhost:8000/location/api/v1/districts',
                dataField: 'data.content',
              },
            },
            {
              key: 'sector',
              label: 'Sector',
              bindlabel: 'name',
              bindvalue: '',
              placeholder: 'Select sector',
              parent: 'district',
              required: true,
              parentBindKey: 'id',
              dataset: {
                url: 'http://localhost:8000/location/api/v1/sectors',
                dataField: 'data.content',
              },
            },
          ],
        },
      },
    },
    {
      label: 'Child ID',
      key: {
        key: 'Child_ID',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          idType: 'CHILD_ID',
          useBaseUrl: true,
          label: 'Child National ID',
          disabled: false,
          readonly: false,
          idLength: CustomIdInputTypesIdLength.CHILD_ID_LENGTH,
          required: true,
          placeholder: 'Provide Child National ID',
          populates: [],
          endpointCode: 'NIDAGETIDINFO',
        },
      },
    },
    {
      label: 'CRVS Data Fetch',
      key: {
        key: 'CRVS',
        type: EIremboFormlyFieldTypes.customcrvsidinput,
        props: {
          label: 'CRVS Data Fetch',
          required: true,
          allowedDocumentTypes: [
            {
              value: 'CHILDID',
              pattern: '^\\d*$',
              minLength: 8,
            },
            {
              value: 'PASSPORTID',
              pattern: '^[a-zA-Z0-9]*$',
              minLength: 5,
            },
          ],
          documentTypeKey: '',
          placeholder: 'Enter identification number',
          useBaseUrl: true,
          endpointCode: 'NIDAGETIDINFO',
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for failed response',
            pattern: 'Error message for pattern',
          },
        },
      },
    },
    {
      label: 'Data Table',
      key: {
        key: 'Data Table',
        type: EIremboFormlyFieldTypes.customdatatable,
        props: {
          label: 'Favourite Services',
          columns: [
            {
              key: 'name',
              title: 'Name',
              sortable: true,
            },
            {
              key: 'gender',
              title: 'Gender',
              sortable: false,
            },
          ],
          items: [
            {
              name: 'Stacy Mark',
              gender: 'Female',
            },
            {
              name: 'John Doe',
              gender: 'Male',
            },
            {
              name: 'Bayo Olatunji',
              gender: 'Male',
            },
          ],
          asyncData: false,
          searchable: true,
          selectable: true,
          minSelection: 1,
          maxSelection: 2,
          emptyMessage: 'No Records',
          disabled: false,
          searchPlaceHolder: 'Enter searck keyword',
          customTableClass: 'customTableClass',
          title: 'Title of the table',
          minimalPagination: false,
          pageSize: 20,
          zeroBasedPageIndex: true,
          currentPageKey: 'page',
          pageSizeKey: 'size',
          searchKeywordKey: 'searchTerm',
          collectionSizeKey: 'totalElements',
          sortKey: 'sort',
          sortDirectionKey: 'sortDirection',
          dataset: {
            httpMethod: 'GET',
            url: '/service/api/v1/public/services',
            headers: {},
            dataField: 'data.content',
            useBaseUrl: true,
            params: {
              sort: 'dateCreated',
              sortDirection: 'asc',
            },
            endpointCode: '',
          },
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {
            minSelection: 'At least one item should be selected',
            required: 'Field is required',
            maxSelection: 'You only allowed to select 2 items from the table',
          },
        },
      },
    },
    {
      label: 'Table with input block',
      key: {
        key: 'Table with input block',
        type: EIremboFormlyFieldTypes.customtablewithinputform,
        props: {
          label: 'Table with input block',
          columns: [
            {
              label: 'Name',
              key: 'name',
              type: 'input',
              required: true,
              unique: true,
            },
            {
              label: 'Date',
              key: 'date',
              type: 'customdatepicker',
            },
          ],
          enableEdit: true,
          enableDeletion: true,
          enableRowSelection: false,
          showSearch: false,
          showPagination: true,
          itemsPerPage: 10,
        },
      },
    },
    {
      label: 'Date Time Picker',
      key: {
        key: 'DatetimePicker',
        type: EIremboFormlyFieldTypes.customdatetimepicker,
        props: {
          label: 'Date time picker',
          disabled: false,
          required: false,
          placeholder: 'Date time picker',
        },
      },
    },
    {
      label: 'Double ID Input WIdget',
      key: {
        key: 'CustomDoubleIdInputHAQS2',
        id: 'formly_422_customdoubleidinput_CustomDoubleIdInputHAQS2_0',
        type: 'customdoubleidinput',
        props: {
          url: '/integration/v1/fetch/sync',
          label: '',
          disabled: false,
          required: true,
          populates: [
            {
              valueKey: 'husbandDetails.surName',
              targetKey: 'TextInputHAQS2',
            },
            {
              valueKey: 'civilStatusOfficeName',
              targetKey: 'Textinput2JEbTQ',
            },
          ],
          useBaseUrl: true,
          placeholder: 'Text Input',
          endpointCode: 'FGETMARRIAGERECORD',
          firstIdentityLabel: 'Husband',
          secondIdentityLabel: 'Wife',
          allowedDocumentTypes: [
            {
              label: 'National ID',
              value: 'NID',
              pattern: '^\\d*$',
              maxLength: 16,
              minLength: 16,
            },
            {
              label: 'NIN',
              value: 'NIN',
              pattern: '^\\d{3}-\\d{4}-\\d{3}$',
              maxLength: 12,
              minLength: 12,
              placeholder: 'Enter NIN (XXX-XXXX-XXX)',
            },
            {
              label: 'Child ID',
              value: 'CHILD_ID',
              pattern: '^\\d*$',
              maxLength: 8,
              minLength: 8,
            },
          ],
          firstIdentityValueKey: 'husbandDocumentNumber',
          firstIdentityPlaceholder: "Husband's Details",
          firstIdentityDocumentTypeKey: 'husbandDocumentType',
          firstIdentityCountryValueKey: 'husbandCountry',
          secondIdentityValueKey: 'wifeDocumentNumber',
          secondIdentityPlaceholder: "Wife's Details",
          secondIdentityDocumentTypeKey: 'wifeDocumentType',
          secondIdentityCountryValueKey: 'wifeCountry',
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {
            required: 'Error message for required input',
            invalidInput: 'Error message for invalid input',
          },
        },
        wrappers: [
          'form-field',
          'field-wrapper',
          'hidden-field-wrapper',
          'hint-field-wrapper',
          'irembogov-readonly-wrapper',
        ],
      },
    },
    {
      label: 'Generic Data Fetch',
      key: {
        key: 'DATAFETCH',
        type: EIremboFormlyFieldTypes.customgenericdatafetch,
        props: {
          label: 'Generic Data Fetch',
          required: true,
          documentTypeKey: 'DocumentTypeComponentKey',
          placeholder: 'Enter identification number',
          useBaseUrl: true,
          useMultiplePayloadKeys: true,
          endpointCode: 'NIDAGETIDINFO',
          populates: [],
          triggerType: 'TRIGGER_BUTTON',
          url: '/integration/v1/fetch/sync',
          isStatic: false,
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for failed response',
            pattern: 'Error message for pattern',
          },
        },
      },
    },
    {
      label: 'Location',
      key: LocationWidgetUtils.buildCascadeField(
        `Country${randomKey}`,
        <string>Object.keys(LOCATION_WIDGET_LEVELS).shift(),
        <string>Object.keys(LOCATION_WIDGET_LEVELS).pop(),
        'Location Widget',
        false,
        false
      ),
    },
    {
      label: 'NID',
      key: {
        key: 'NID',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          useBaseUrl: true,
          label: 'National ID',
          disabled: false,
          readonly: false,
          idLength: CustomIdInputTypesIdLength.NID_ID_LENGTH,
          idType: 'NID',
          required: true,
          placeholder: 'Provide National ID',
          populates: [],
          endpointCode: 'NIDAGETIDINFO',
        },
      },
    },
    {
      label: 'NIN',
      key: {
        key: 'NIN',
        type: EIremboFormlyFieldTypes.customidinput,
        props: {
          label: 'NIN Number',
          placeholder: 'Provide NIN Number',
          required: true,
          readonly: false,
          url: '/identity/v1/nin-info-by-username',
          useBaseUrl: true,
          disabled: false,
          idLength: CustomIdInputTypesIdLength.NIN_ID_LENGTH,
          populateRelativeToRootForm: false,
          populates: [],
        },
        validation: {
          messages: {
            required: 'Error message for `required`',
            invalidInput: 'Error message for invalid input',
            invalidId: 'Error message for invalid NIN',
          },
        },
      },
    },
    {
      label: 'Slot Booking',
      key: buildSlotWidgetField('Slot booking'),
    },
    {
      label: 'TIN',
      key: {
        key: 'TIN',
        type: EIremboFormlyFieldTypes.customtininput,
        defaultValue: null,
        props: {
          endpointCode: 'FTINDETAILS',
          hint: '',
          label: 'TIN',
          hidden: false,
          tooltip: '',
          disabled: false,
          required: false,
          populates: [],
          placeholder: 'Enter TIN',
          defaultRequired: false,
          internalDescription: '',
        },
        validation: {
          messages: {
            required: 'Please fill in this field',
            invalidTIN: 'No details found for supplier',
            invalidInput: 'TIN number must be 9 digits',
          },
        },
      },
    },
    {
      label: 'OTP Data Fetch',
      key: {
        key: 'otpgeneric',
        type: EIremboFormlyFieldTypes.customotpdatafetch,
        props: {
          url: '/integration/v1/fetch/sync',
          label: 'OTP Demo',
          disabled: false,
          required: false,
          otpLength: 5,
          populates: [],
          payloadKey: 'payloadKey',
          useBaseUrl: true,
          placeholder: 'Enter email, telephone(078****) or application number',
          endpointCode: 'TEST_FETCH',
          allowOtpNumbersOnly: false,
        },
        validation: {
          show: true,
          messages: {
            required: 'field is required',
            otpExpired: 'OTP expired.',
            otpInvalid: 'Please enter a valid OTP.',
            otpRequired: 'Please enter the OTP digits sent to the recipient.',
            dataFetchFailed: 'Failed to complete data fetch. Please try again.',
            dataFetchPending: 'Please wait while the information is retrieved.',
            requestOtpFailed: 'Failed sending OTP request. Please try again.',
            pendingRequestOtp:
              'Request for an OTP to be sent to the recipient to proceed.',
            recipientNotFound: 'Recipient not found',
            invalidRecipientEntry: 'Please enter a valid recipient',
            otpVerificationPending:
              'Please submit the OTP for verification to proceed.',
            endpointCodeNotConfigured:
              'Endpoint code is required on this field.',
            otpVerificationRequestFailed: 'OTP verification failed.',
          },
        },
      },
    },
  ];
}

function toFbPaletteItems(
  source: IIrembogovBasicLabelKeyPair<FormlyFieldConfig>[],
  category: 'field' | 'widget'
): IFbPaletteItem[] {
  return source.map(item => ({
    displayName: item.label,
    type: item.key?.type?.toString() || '',
    category,
    icon: FB_PALETTE_ITEM_ICON,
    config: item.key,
  }));
}

export function getFbFieldPaletteItems(): IFbPaletteItem[] {
  return toFbPaletteItems(buildFieldPaletteSource(), 'field');
}

export function getFbWidgetPaletteItems(): IFbPaletteItem[] {
  return toFbPaletteItems(buildWidgetPaletteSource(), 'widget');
}
